import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Eye,
  Search,
  Shield,
  Users,
  Settings,
  Mail,
  UserCheck,
  UserX,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CreditManagementPanel } from './CreditManagementPanel';

interface UserProfile {
  id: string;
  user_id: string;
  email: string | null;
  is_admin: boolean | null;
  is_active: boolean | null;
  subscription: string | null;
  license_id: string | null;
  api_keys_configured: boolean | null;
  created_at: string;
  updated_at: string;
  last_login: string | null;
  trial_expires_at: string | null;
  api_usage_current_month: number | null;
  override_license_check: boolean | null;
  keap_tags: string[] | null;
}

interface License {
  id: string;
  name: string;
  description: string | null;
  max_calls_per_month: number | null;
  max_campaigns: number | null;
  is_active: boolean | null;
  features: any;
}

export function UsersManagement() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
    fetchLicenses();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenses = async () => {
    try {
      const { data, error } = await supabase
        .from('licenses')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLicenses(data || []);
    } catch (error: any) {
      console.error('Failed to fetch licenses:', error);
    }
  };

  const handleViewUser = (user: UserProfile) => {
    setSelectedUser(user);
    setIsUserDialogOpen(true);
  };

  const toggleAdmin = async (user: UserProfile) => {
    if (
      !confirm(
        `Are you sure you want to ${
          user.is_admin ? 'remove admin privileges from' : 'make admin'
        } ${user.email}?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !user.is_admin })
        .eq('user_id', user.user_id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `${user.email} ${
          user.is_admin ? 'removed from admin' : 'made admin'
        } successfully`,
      });

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id ? { ...u, is_admin: !u.is_admin } : u
        )
      );
      if (selectedUser?.user_id === user.user_id) {
        setSelectedUser({ ...selectedUser, is_admin: !selectedUser.is_admin });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleActive = async (user: UserProfile) => {
    if (
      !confirm(
        `Are you sure you want to ${
          user.is_active ? 'disable' : 'enable'
        } access for ${user.email}?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      // Get session once at the top
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Failed to get auth session');
      }
      const accessToken = session.access_token;

      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ is_active: !user.is_active })
        .eq('user_id', user.user_id);

      if (profileError) throw profileError;

      // Call the edge function
      const edgeResponse = await fetch(
        'https://giedkdxtrqcqzcockacd.supabase.co/functions/v1/disable-user',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            ban: user.is_active, // true to disable, false to enable
          }),
        }
      );

      if (!edgeResponse.ok) {
        const errorData = await edgeResponse.json();
        throw new Error(
          errorData.error || 'Failed to update user status via edge function'
        );
      }

      // Refresh credit balances if needed
      await supabase.functions.invoke('get-credits', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      toast({
        title: 'Success',
        description: `${user.email} ${
          user.is_active ? 'disabled' : 'enabled'
        } successfully`,
      });

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id ? { ...u, is_active: !u.is_active } : u
        )
      );
      if (selectedUser?.user_id === user.user_id) {
        setSelectedUser({
          ...selectedUser,
          is_active: !selectedUser.is_active,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const sendPasswordReset = async (user: UserProfile) => {
    if (!user.email) {
      toast({
        title: 'Error',
        description: 'No email address available for this user',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/login`,
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Password reset email sent to ${user.email}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const assignLicense = async (user: UserProfile, licenseId: string | null) => {
    const license = licenses.find((l) => l.id === licenseId);
    const licenseInfo = license
      ? `${license.name} (${license.max_calls_per_month || 0} monthly credits)`
      : 'No License';

    if (
      !confirm(
        `Assign ${licenseInfo} to ${user.email}? This will update their credit allocations and may grant bonus credits.`
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      // Update license assignment
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ license_id: licenseId, subscription: license?.name || null })
        .eq('user_id', user.user_id);

      if (updateError) throw updateError;

      // Update monthly base credits via adjust-credits (set to new max or zero)
      if (licenseId !== null) {
        // Skip if removing license, or handle reset to 0 if desired
        // Fetch current monthly_base balance
        const { data: currentBalanceData, error: fetchError } = await supabase
          .from('credit_balances')
          .select('balance')
          .eq('user_id', user.user_id)
          .eq('credit_type', 'monthly_base')
          .maybeSingle(); // Use maybeSingle() to handle no row gracefully

        if (fetchError) throw fetchError;

        const currentBalance = currentBalanceData?.balance || 0;
        const newMax = license?.max_calls_per_month || 0;
        const amount = newMax - currentBalance; // Delta to set exactly to new max

        const { error: creditsError } = await supabase.functions.invoke(
          'adjust-credits',
          {
            body: {
              user_id: user.user_id,
              credit_type: 'monthly_base',
              amount: amount,
              description: `Monthly base credits reset for license ${
                license?.name || 'assignment'
              }`,
              admin_notes: `Automatically adjusted during license assignment to ${licenseId}`,
            },
          }
        );

        if (creditsError) {
          console.error('Failed to adjust monthly credits:', creditsError);
          toast({
            title: 'Warning',
            description: `License assigned but failed to adjust monthly credits: ${creditsError.message}`,
            variant: 'destructive',
          });
        }
      }

      // Grant bonus credits if license has them
      if (license && license.features?.credit_settings?.bonus_credits > 0) {
        const { error: bonusError } = await supabase.functions.invoke(
          'grant-bonus-credits',
          {
            body: {
              user_id: user.user_id,
              amount: license.features.credit_settings.bonus_credits,
              expiration_days:
                license.features.credit_settings.bonus_expiration_days || 0,
              description: `License bonus credits from ${license.name}`,
              admin_notes: `Automatically granted when assigning ${license.name} license`,
            },
          }
        );

        if (bonusError) {
          console.error('Failed to grant bonus credits:', bonusError);
          toast({
            title: 'Warning',
            description: `License assigned but failed to grant bonus credits: ${bonusError.message}`,
            variant: 'destructive',
          });
        }
      }

      // Refresh credit balances by calling get-credits
      await supabase.functions.invoke('get-credits', {
        headers: {
          Authorization: `Bearer ${
            (
              await supabase.auth.getSession()
            ).data.session?.access_token
          }`,
        },
      });

      toast({
        title: 'Success',
        description: `License ${license ? 'assigned' : 'removed'} successfully${
          license?.features?.credit_settings?.bonus_credits > 0
            ? ' with bonus credits granted'
            : ''
        }`,
      });

      // Update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id ? { ...u, license_id: licenseId } : u
        )
      );
      if (selectedUser?.user_id === user.user_id) {
        setSelectedUser({ ...selectedUser, license_id: licenseId });
        console.log('selected user:', selectedUser);
        console.log('license id:', licenseId);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getCurrentLicense = (user: UserProfile) => {
    return licenses.find((l) => l.id === user.license_id);
  };

  const toggleWhiteLabel = async (user: UserProfile) => {
    const currentLicense = getCurrentLicense(user);
    if (!currentLicense) {
      toast({
        title: 'No License',
        description: 'Please assign a license to this user first',
        variant: 'destructive',
      });
      return;
    }

    const currentFeatures = currentLicense.features || {};
    const newWhiteLabelStatus = !currentFeatures.white_label;

    if (
      !confirm(
        `${newWhiteLabelStatus ? 'Grant' : 'Remove'} white-label feature ${
          newWhiteLabelStatus ? 'to' : 'from'
        } ${user.email}?`
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      // Update the license features
      const updatedFeatures = {
        ...currentFeatures,
        white_label: newWhiteLabelStatus,
      };

      const { error } = await supabase
        .from('licenses')
        .update({ features: updatedFeatures })
        .eq('id', currentLicense.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `White-label feature ${
          newWhiteLabelStatus ? 'granted to' : 'removed from'
        } ${user.email}`,
      });

      // Update local license state
      setLicenses((prev) =>
        prev.map((l) =>
          l.id === currentLicense.id ? { ...l, features: updatedFeatures } : l
        )
      );
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.user_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Users className='h-5 w-5' />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='animate-pulse space-y-4'>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className='h-16 bg-muted rounded'></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Users className='h-5 w-5' />
            User Management
          </CardTitle>
          <CardDescription>
            View and manage user accounts, credits, and permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='mb-6'>
            <div className='relative'>
              <Search className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search users by email or ID...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>

          <div className='space-y-4'>
            {filteredUsers.map((user) => (
              <div key={user.id} className='border rounded-lg p-4'>
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2 mb-2'>
                      <h3 className='font-semibold'>
                        {user.email || 'No Email'}
                      </h3>
                      {user.is_admin && (
                        <Badge variant='destructive' className='text-xs'>
                          <Shield className='h-3 w-3 mr-1' />
                          Admin
                        </Badge>
                      )}
                      <Badge
                        variant={user.is_active ? 'default' : 'secondary'}
                        className='text-xs'
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {user.override_license_check && (
                        <Badge variant='outline' className='text-xs'>
                          License Override
                        </Badge>
                      )}
                    </div>

                    <div className='grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground'>
                      <div>
                        <div className='font-medium'>Subscription</div>
                        <div>{user.subscription || 'None'}</div>
                      </div>
                      <div>
                        <div className='font-medium'>API Usage</div>
                        <div>
                          {user.api_usage_current_month || 0} this month
                        </div>
                      </div>
                      <div>
                        <div className='font-medium'>Last Login</div>
                        <div>{formatDate(user.last_login)}</div>
                      </div>
                      <div>
                        <div className='font-medium'>Joined</div>
                        <div>{formatDate(user.created_at)}</div>
                      </div>
                    </div>
                  </div>

                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handleViewUser(user)}
                    >
                      <Eye className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {filteredUsers.length === 0 && (
              <div className='text-center py-8 text-muted-foreground'>
                {searchTerm ? 'No users match your search.' : 'No users found.'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              {selectedUser?.email || 'User'} - Complete account information and
              credit management
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <Tabs defaultValue='credits' className='w-full'>
              <TabsList className='grid w-full grid-cols-4'>
                <TabsTrigger value='credits'>Credit Management</TabsTrigger>
                <TabsTrigger value='profile'>Profile Details</TabsTrigger>
                <TabsTrigger value='admin'>Admin Actions</TabsTrigger>
                <TabsTrigger value='activity'>Activity Log</TabsTrigger>
              </TabsList>

              <TabsContent value='credits' className='space-y-4'>
                <CreditManagementPanel
                  userId={selectedUser.user_id}
                  userEmail={selectedUser.email}
                />
              </TabsContent>

              <TabsContent value='profile' className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <h4 className='font-medium mb-2'>Account Information</h4>
                    <div className='space-y-2 text-sm'>
                      <div>
                        <strong>User ID:</strong> {selectedUser.user_id}
                      </div>
                      <div>
                        <strong>Email:</strong>{' '}
                        {selectedUser.email || 'Not provided'}
                      </div>
                      <div>
                        <strong>Subscription:</strong>{' '}
                        {selectedUser.subscription || 'None'}
                      </div>
                      <div>
                        <strong>License ID:</strong>{' '}
                        {selectedUser.license_id || 'None'}
                      </div>
                      <div>
                        <strong>Admin:</strong>{' '}
                        {selectedUser.is_admin ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>Active:</strong>{' '}
                        {selectedUser.is_active ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>API Keys Configured:</strong>{' '}
                        {selectedUser.api_keys_configured ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>License Override:</strong>{' '}
                        {selectedUser.override_license_check ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className='font-medium mb-2'>Usage & Dates</h4>
                    <div className='space-y-2 text-sm'>
                      <div>
                        <strong>API Usage (Month):</strong>{' '}
                        {selectedUser.api_usage_current_month || 0}
                      </div>
                      <div>
                        <strong>Created:</strong>{' '}
                        {formatDate(selectedUser.created_at)}
                      </div>
                      <div>
                        <strong>Updated:</strong>{' '}
                        {formatDate(selectedUser.updated_at)}
                      </div>
                      <div>
                        <strong>Last Login:</strong>{' '}
                        {formatDate(selectedUser.last_login)}
                      </div>
                      <div>
                        <strong>Trial Expires:</strong>{' '}
                        {formatDate(selectedUser.trial_expires_at)}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedUser.keap_tags &&
                  selectedUser.keap_tags.length > 0 && (
                    <div>
                      <h4 className='font-medium mb-2'>Keap Tags</h4>
                      <div className='flex flex-wrap gap-1'>
                        {selectedUser.keap_tags.map((tag, index) => (
                          <Badge
                            key={index}
                            variant='outline'
                            className='text-xs'
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </TabsContent>

              <TabsContent value='admin' className='space-y-6'>
                <div>
                  <h4 className='font-medium mb-4 flex items-center gap-2'>
                    <Settings className='h-4 w-4' />
                    Admin Actions
                  </h4>

                  <div className='grid grid-cols-2 gap-4'>
                    {/* Admin Toggle */}
                    <div className='space-y-2'>
                      <h5 className='text-sm font-medium'>
                        Administrator Access
                      </h5>
                      <Button
                        variant={
                          selectedUser.is_admin ? 'destructive' : 'default'
                        }
                        onClick={() => toggleAdmin(selectedUser)}
                        disabled={actionLoading}
                      >
                        {selectedUser.is_admin ? (
                          <>
                            <UserX className='h-4 w-4 mr-2' />
                            Remove Admin
                          </>
                        ) : (
                          <>
                            <UserCheck className='h-4 w-4 mr-2' />
                            Make Admin
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Active Toggle */}
                    <div className='space-y-2'>
                      <h5 className='text-sm font-medium'>Account Status</h5>
                      <Button
                        variant={
                          selectedUser.is_active ? 'destructive' : 'default'
                        }
                        onClick={() => toggleActive(selectedUser)}
                        disabled={actionLoading}
                      >
                        {selectedUser.is_active ? (
                          <>
                            <UserX className='h-4 w-4 mr-2' />
                            Disable Account
                          </>
                        ) : (
                          <>
                            <UserCheck className='h-4 w-4 mr-2' />
                            Enable Account
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Password Reset */}
                    <div className='space-y-2'>
                      <h5 className='text-sm font-medium'>Password Reset</h5>
                      <Button
                        variant='outline'
                        onClick={() => sendPasswordReset(selectedUser)}
                        disabled={actionLoading || !selectedUser.email}
                      >
                        <Mail className='h-4 w-4 mr-2' />
                        Send Reset Email
                      </Button>
                    </div>

                    {/* License Assignment */}
                    <div className='space-y-2'>
                      <h5 className='text-sm font-medium'>
                        License Assignment
                      </h5>
                      <Select
                        value={selectedUser.license_id || 'none'}
                        onValueChange={(value) =>
                          assignLicense(
                            selectedUser,
                            value === 'none' ? null : value
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select license...' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='none'>No License</SelectItem>
                          {licenses.map((license) => (
                            <SelectItem key={license.id} value={license.id}>
                              {license.name} ({license.max_calls_per_month || 0}{' '}
                              credits)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getCurrentLicense(selectedUser) && (
                        <div className='text-xs text-muted-foreground'>
                          Current: {getCurrentLicense(selectedUser)?.name}
                        </div>
                      )}
                    </div>

                    {/* White-label Feature Toggle */}
                    <div className='space-y-2'>
                      <h5 className='text-sm font-medium'>
                        White-label Feature
                      </h5>
                      <Button
                        variant='outline'
                        onClick={() => toggleWhiteLabel(selectedUser)}
                        disabled={actionLoading}
                      >
                        {selectedUser.license_id &&
                        getCurrentLicense(selectedUser)?.features
                          ?.white_label ? (
                          <>
                            <UserX className='h-4 w-4 mr-2' />
                            Remove White-label
                          </>
                        ) : (
                          <>
                            <UserCheck className='h-4 w-4 mr-2' />
                            Grant White-label
                          </>
                        )}
                      </Button>
                      <p className='text-xs text-muted-foreground'>
                        Allows user to use custom domains for client portals
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value='activity' className='space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  Activity logging coming soon
                </p>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UsersManagement;
